import { useTranslations } from '@vocab/react';
import { Button, Actions, IconSettings } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

import type { IJob } from '../../../../stores/JobStore/Job';
import DropdownMenu from '../../../common/DropdownMenu';
import TalentSearchLink from '../TalentSearchLink/TalentSearchLink';
import type { JobAction } from '../actions';
import { useJobActionOptions } from '../hooks/useJobActionOptions';

import translations from './.vocab/';

interface Props {
  job: IJob;
  isRecommendedCandidatesTabShown: boolean;
  onAction: (jobAction: JobAction) => void;
  dropdownMenuId: string;
}

const JobActionDesktop: React.FC<Props> = ({
  job,
  isRecommendedCandidatesTabShown,
  onAction,
  dropdownMenuId,
}) => {
  const [isReadyOnClient, setIsReadyOnClient] = React.useState(false);
  const [checked, setChecked] = useState(false);
  React.useEffect(() => {
    setIsReadyOnClient(true);
  }, []);

  const { t } = useTranslations(translations);
  const { site } = useAppConfig();
  const options = useJobActionOptions({
    job,
    site,
    showTalentSearchLink: false,
    showExportToCsv: job.userStore.user.canExportToCsv,
  });

  if (!isReadyOnClient) {
    return null;
  }

  return (
    <Actions>
      {job.isActive && !isRecommendedCandidatesTabShown && <TalentSearchLink />}
      <DropdownMenu
        checked={checked}
        setChecked={setChecked}
        options={options}
        onClick={(value: string) => onAction(value as JobAction)}
        width="380px"
        checkboxId={dropdownMenuId}
        aria-label={t('Manage job')}
      >
        <div data-testid={dropdownMenuId}>
          <Button
            data={{ intercomid: 'manageJob' }}
            size="small"
            tone="neutral"
            variant="soft"
            aria-label={t('Manage job')}
          >
            <IconSettings tone="secondary" />
          </Button>
        </div>
      </DropdownMenu>
    </Actions>
  );
};

export default observer(JobActionDesktop);
